import type { CSSProperties } from "react";
import { cn } from "../lib/utils";

interface WaveformProps {
  bars?: number;
  playing?: boolean;
  progress?: number; // 0–1
  color?: string;
  activeColor?: string;
  height?: number;
  className?: string;
  style?: CSSProperties;
  onSeek?: (progress: number) => void;
}

// Generate stable-looking pseudorandom bars
function makeBars(n: number) {
  const bars: number[] = [];
  let v = 0.5;
  for (let i = 0; i < n; i++) {
    v = Math.min(1, Math.max(0.1, v + (Math.sin(i * 0.7) * 0.3 + Math.sin(i * 1.3) * 0.2)));
    bars.push(v);
  }
  return bars;
}

const defaultBars = makeBars(60);

export function Waveform({
  bars: barCount = 60,
  playing = false,
  progress = 0,
  color,
  activeColor,
  height = 48,
  className,
  style,
  onSeek,
}: WaveformProps) {
  const bars = barCount === 60 ? defaultBars : makeBars(barCount);

  return (
    <div
      className={cn("wf-root", playing && "wf-playing", className)}
      style={{ "--wf-h": `${height}px`, "--wf-color": color ?? "var(--border)", "--wf-active": activeColor ?? "var(--primary)", ...style } as CSSProperties}
      role="progressbar"
      aria-valuenow={Math.round(progress * 100)}
      aria-valuemin={0}
      aria-valuemax={100}
      onClick={e => {
        if (!onSeek) return;
        const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
        onSeek((e.clientX - rect.left) / rect.width);
      }}
      style={{ cursor: onSeek ? "pointer" : "default", ...style } as CSSProperties}
    >
      {bars.map((h, i) => {
        const isActive = i / bars.length < progress;
        return (
          <span
            key={i}
            className={cn("wf-bar", isActive && "wf-bar-active", playing && isActive && "wf-bar-pulse")}
            style={{ "--wf-bar-h": `${h * 100}%` } as CSSProperties}
          />
        );
      })}
    </div>
  );
}
